import { Box, Tooltip, IconButton, Grid, Typography, Chip, Divider, Paper } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import _ from 'lodash'
import { ROWS_PER_PAGE_OPTIONS } from 'constants'
import { capitalize } from 'lodash';
import { BasePathProvider, useBasePath } from "components/Providers";
import Header from "components/Header";
import InfoIcon from '@mui/icons-material/Info';
import dayjs from "dayjs";

function AssetDetailField({ label, value }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
            <Typography variant="body2" fontWeight={500}>{value || '—'}</Typography>
        </Box>
    )
}

export default function AssetLogList({ assetLogs }) {
    const { t } = useTranslation();
    const basePath = useBasePath();

    const asset = assetLogs[0]?.asset ?? {}

    const tableColumns = [
        {
            field: 'action',
            headerName: t('Action'),
            editable: false,
            sortable: false,
            width: 110,
            renderCell: (params) => capitalize(params.row.action || ' ')
        },
        {
            field: 'logDate',
            headerName: t('Log Date'),
            editable: false,
            sortable: false,
            width: 160,
            renderCell: (params) => params.row.logDate ? dayjs(params.row.logDate).format('MM/DD/YY (h:mm A)') : ''
        },
        {
            field: 'assigneeName',
            headerName: t('Assignee'),
            editable: false,
            sortable: false,
            width: 180,
            renderCell: (params) => params.row.assignInfo?.assigneeName || ''
        },
        {
            field: 'personName',
            headerName: t('Staff'),
            editable: false,
            sortable: false,
            width: 180,
            renderCell: (params) => params.row.assignInfo?.personName || ''
        },
        {
            field: 'note',
            headerName: t('Notes'),
            editable: false,
            sortable: false,
            flex: 1,
            minWidth: 200,
            renderCell: (params) => {
                const note = params?.row?.note || '';
                if (!note) return null;
                return (
                    <Tooltip title={note} placement="bottom">
                        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                            {note}
                        </Box>
                    </Tooltip>
                );
            }
        },
        {
            field: 'info',
            headerName: '',
            width: 50,
            editable: false,
            sortable: false,
            renderCell: (params) => {
                const dataContent = JSON.stringify(params.row.data, null, 2);
                return (
                    <Tooltip
                        title={
                            <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                                {dataContent}
                            </pre>
                        }
                        placement="right"
                    >
                        <IconButton size="small">
                            <InfoIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                );
            }
        },
    ]

    const pageSizeOptions = _.uniq([...ROWS_PER_PAGE_OPTIONS])

    return (
        <BasePathProvider value={`${basePath}`}>
            <Box sx={{ mb: 2 }}>
                <Header title="Asset Logs" small close="." borderBottom />
            </Box>

            <Paper variant="outlined" sx={{ mx: 2, my: 2, p: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={3}>
                        <AssetDetailField label={t('Brand')} value={capitalize(asset.brand || '')} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <AssetDetailField label={t('Model')} value={asset.model} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <AssetDetailField label={t('Asset Number')} value={asset.assetNumber} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <AssetDetailField label={t('Serial Number')} value={asset.serialNumber} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block">{t('Status')}</Typography>
                            <Chip
                                label={capitalize(asset.statusId || '')}
                                color="default"
                                size="small"
                                sx={{ mt: 0.25 }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            <Divider />

            <Box p={1} sx={{ overflowX: 'auto' }}>
                <DataGrid
                    columns={tableColumns}
                    rows={assetLogs.map((log, index) => ({ ...log, uniqueId: `log-${index}` }))}
                    getRowId={(row) => row.uniqueId}
                    pageSizeOptions={pageSizeOptions}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    autoHeight
                    hideFooter
                />
            </Box>
        </BasePathProvider>
    )
}